/**
 * What a form can check for itself, while somebody is still typing.
 *
 * These rules are a deliberate second copy of the ones in
 * `backend/apps/common/people.py`, and the duplication is the point rather
 * than an oversight. The counter needs to know a phone number is wrong before
 * pressing Save, not after a round trip — but the browser is not trusted, so
 * the server still runs every one of these and its answer wins. If the two
 * ever disagree, the server is right and this file is the bug.
 *
 * Kept small on purpose. Anything that needs the database — is this licence
 * already held, is this customer blacklisted — cannot be answered here and is
 * not attempted.
 */

/** A Nepali mobile: ten digits beginning with 9. */
const PHONE_DIGITS = 10;

/** Nepal's country code. Local numbers are held to the local rule. */
const NEPAL = '977';

/** E.164: nothing real is shorter than six digits or longer than fifteen. */
const SHORTEST_INTERNATIONAL = 6;
const LONGEST_INTERNATIONAL = 15;

/** Two characters, because "Om" is a whole first name. */
const SHORTEST_NAME = 2;

/** Enough of a licence to be checked against the card in somebody's hand. */
const SHORTEST_LICENCE = 6;

/** The digits of a Nepali number, however it was written. */
function digitsOf(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  return digits.startsWith(NEPAL)
    ? digits.slice(NEPAL.length).replace(/^0+/, '')
    : digits.replace(/^0+/, '');
}

/**
 * A customer's, driver's or enquirer's number, from anywhere.
 *
 * Nepali numbers keep the local rule — ten digits starting with a 9 — because
 * this is the country the business operates in and a wrong local number is a
 * booking nobody can confirm. Everything else is checked for length only: per
 * country rules change, and a stale one refuses somebody standing at the
 * counter with money.
 */
export function checkPhone(raw: string): string | undefined {
  const all = raw.replace(/\D/g, '');
  if (all === '') return 'Enter a phone number.';

  // No country code typed at all means a local number, which is what the
  // phone box sends when nobody touches the dropdown.
  if (!raw.trimStart().startsWith('+') || all.startsWith(NEPAL)) {
    const digits = digitsOf(raw);
    if (digits.length !== PHONE_DIGITS) {
      return `A Nepali mobile is ${String(PHONE_DIGITS)} digits, like 9801234567. That is ${String(digits.length)}.`;
    }
    if (!digits.startsWith('9')) {
      return `A Nepali mobile starts with 9. That starts with ${digits[0] ?? ''}.`;
    }
    return undefined;
  }

  if (all.length < SHORTEST_INTERNATIONAL) {
    return `That is too short — ${String(all.length)} digits including the country code.`;
  }
  if (all.length > LONGEST_INTERNATIONAL) {
    return `That is ${String(all.length)} digits. No number anywhere is longer than ${String(LONGEST_INTERNATIONAL)}, country code included.`;
  }
  return undefined;
}

export function checkName(raw: string): string | undefined {
  const name = raw.trim().replace(/\s+/g, ' ');
  if (name === '') return 'Enter a name.';
  if (name.length < SHORTEST_NAME) {
    return `A name is at least ${String(SHORTEST_NAME)} letters. That is one.`;
  }
  // "Contains a letter", not "contains no digits" — this list holds corporate
  // customers, and "G4S Security" is what that business is called. Unicode
  // aware, so a name written in Devanagari passes.
  if (!/\p{L}/u.test(name)) {
    return `A name has letters in it. “${name}” has none — if that is a phone number, it goes in the phone box.`;
  }
  return undefined;
}

/** Blank is allowed and means "not recorded". Rubbish is not. */
export function checkLicence(raw: string): string | undefined {
  const licence = raw.trim();
  if (licence === '') return undefined;

  const characters = licence.replace(/[^0-9a-z]/gi, '');
  if (characters.length < SHORTEST_LICENCE) {
    return 'A licence number looks like 03-06-00123456. That is too short to be one.';
  }
  if (!/\d/.test(characters)) return `A licence number has digits in it. “${licence}” has none.`;
  return undefined;
}

/** Something has to be there. Used where the only rule is "not empty". */
export function checkPresent(raw: string, what: string): string | undefined {
  return raw.trim() === '' ? what : undefined;
}

/**
 * The window a booking covers.
 *
 * Reported from the counter: Out could be set to next week and Back to
 * yesterday, and the form took it — the refusal only came back from the
 * database constraint, by which time the customer was still on the phone.
 *
 * Back-dating is deliberately *not* refused. Entering a walk-in that already
 * happened is normal here, so a booking starting in the past is ordinary
 * work rather than a mistake to be blocked.
 */
export function checkWindow(startAt: string, endAt: string): string | undefined {
  if (startAt === '' || endAt === '') return undefined;

  const out = new Date(startAt).getTime();
  const back = new Date(endAt).getTime();
  if (Number.isNaN(out) || Number.isNaN(back)) return undefined;

  if (back === out) return 'Back is the same moment as Out. A booking has to last some time.';
  if (back < out) return 'Back is before Out. Check the dates.';
  return undefined;
}

/** A Nepali PAN, and the VAT number of a registered business, are both nine digits. */
const PAN_DIGITS = 9;

/**
 * A tax number, checked before it reaches a document.
 *
 * Blank means "not given", which is the normal case — a walk-in tourist has no
 * Nepali PAN. What is caught is a number that is present and wrong, because
 * that one prints on an invoice under the customer's name.
 */
export function checkPan(raw: string): string | undefined {
  const pan = raw.replace(/\s/g, '');
  if (pan === '') return undefined;
  if (!/^\d+$/.test(pan) || pan.length !== PAN_DIGITS) {
    return `A PAN is ${String(PAN_DIGITS)} digits, like 301234567. That is ${String(pan.length)}.`;
  }
  return undefined;
}
